import Vue from "vue";
import Vuex, { Commit, Dispatch } from "vuex";
import User from "../classes/user";
import Fleet from "../classes/fleet";
import Charger from "../classes/charger";
import Trip from "../classes/trip";
import EVModel from "../classes/evModel";
import {
  deleteSavedTrip,
  fetchChargingStationsDb,
  fetchEVModelImageLinks,
  fetchFleetData,
  fetchFleetVehicles,
  fetchNetworkBranding,
  fetchSavedTrips,
  fetchUser,
  fetchSettings,
  login,
  logout,
  resetPassword,
  updateUser,
  deleteFile,
  fetchEVmodels,
  updateSetting,
  fetchManagedContent,
  fetchFleetConfigData,
  fetchDriverData,
  fetchFolderDataByName,
  createDirectusFolder,
  updateVehicle,
} from "../api/calls/directus_calls";
import OptimiserDefaultData from "../data/optimiserDefaultData";
import Vehicle from "../classes/vehicle";
import {
  TripFallbackCauseReasons,
  type TripPlanningFormData,
  type TripStats,
} from "../types/trip_specific_types";
import type {
  Car_User,
  DataToUpdate,
  DirectusLoginParams,
  ManagedContent,
} from "../types/directus_types";
import {
  ActionTypes,
  AsyncStatus,
  CalcVsType,
  ChargerFilters,
  GettersTypes,
  MainDialogContent,
  MutationTypes,
  SecondaryDialogContent,
  State,
  TertiaryDialogContent,
  UploadVehicleImagePayload,
} from "./store_types";
import Driver from "../classes/driver";
import { fetchGeoIPDetails } from "../api/calls/maps_little_monkey_calls";
import GeoLocation from "../classes/geoLocation";
import evNavDefaultData from "../data/eVNavDefaultData";
import TripLocation from "../classes/tripLocation";
import calcFiveYearSavings, {
  FiveYearSavings,
} from "../utils/calcFiveYearSavings";
import { EVNavNearbyParams } from "../types/ev_nav_types";
import NearbyController from "../classes/nearbyController";
import NetworkBranding from "../classes/networkBranding";
import { VehicleType } from "../types/vehicle_specific_types";
import router, { RouteNames } from "../router";
import { Saved_Trips } from "../data/const";
import TspTrip, { TspTripPlanningStatus } from "../classes/tspTrip";
import {
  deleteSavedOptimisedTrip,
  fetchSavedOptimisedTripsByUser,
} from "../api/calls/directus_calls/savedOptimisedTrips";
import {
  createDriverRecord,
  updateDriverRecord,
} from "../api/calls/directus_calls/driverCalls";
import {
  deleteSavedRoutePlans,
  fetchSavedRoutePlansByUser,
} from "../api/calls/directus_calls/savedRoutePlans";

Vue.use(Vuex);

export default new Vuex.Store({
  state: (): State => ({
    // Class object data.
    user: undefined,
    driver: undefined,
    userGeoIPData: undefined,
    fleet: undefined,
    chargers: [],
    trips: [],
    evModels: [],
    networkBranding: [],
    vehicles: [],
    managedContent: [],
    // Ui viewing object data selections.
    selectedTrip: undefined,
    selectedVehicle: undefined,
    selectedCharger: undefined,
    selectedLocation: undefined,
    queuedTripForDelete: undefined,
    viewedFleetVehicle: undefined,
    // async statuses.
    authStatus: undefined,
    updateImageStatus: undefined,
    deleteTripStatus: undefined,
    routePlanningStatus: undefined,
    tripSavingStatus: undefined,
    updatingUserStatus: undefined,
    vehicleSavingStatus: undefined,
    nearbyFetchingFlag: false,
    radarFetchingFlag: false,
    // Ui flags.
    settingsFetchingFlag: true,
    chargerFetchingFlag: true,
    userFetchingFlag: true,
    fleetFetchingFlag: true,
    tripFetchingFlag: true,
    checkingToken: true,
    authorizedFlag: false,
    routePlanningFlag: false,
    showLogin: true,
    mainDialogContent: undefined,
    secondaryDialogContent: undefined,
    tertiaryDialogContent: undefined,
    displayAllChargersAlongRoute: false,
    displayLongestStep: false,
    showNearbyChargersOnly: false,
    showPrivateACChargers: true,
    showPrivateDCChargers: true,
    showPublicACChargers: true,
    showPublicDCChargers: true,
    chargersSearchCriteria: undefined,
    vehiclesSearchCriteria: undefined,
    displayConnectedVehicles: false,
    // settings.
    settingsTableID: undefined,
    SOCMax: OptimiserDefaultData.SOCMax,
    SOCMin: OptimiserDefaultData.SOCMin,
    SOCAct: OptimiserDefaultData.SOCAct,
    SOCEnd: OptimiserDefaultData.SOCEnd,
    defaultHomeCostPerKWh: OptimiserDefaultData.kWhCostHome,
    defaultPublicCostPerKWh: OptimiserDefaultData.kWhCostPublic,
    defaultCostPerMinDC: OptimiserDefaultData.electricityCostPerMinPublic,
    networkSelectionOverride: undefined,
    dieselCostPerLitre: OptimiserDefaultData.dieselCostPerLitre,
    dieselKmPerLitre: OptimiserDefaultData.dieselKmPerLitre,
    extraWeight: undefined,
    perAnnumMaintenanceCurrentV:
      OptimiserDefaultData.perAnnumMaintenanceCurrentV,
    perAnnumMaintenanceEV: OptimiserDefaultData.perAnnumMaintenanceEV,
    petrolCostPerLitre: OptimiserDefaultData.petrolCostPerLitre,
    petrolKmPerLitre: OptimiserDefaultData.petrolKmPerLitre,
    relativeSpeed: OptimiserDefaultData.relativeSpeed,
    roadUserCharges: OptimiserDefaultData.roadUserCharges,
    calcVs: CalcVsType.PETROL,
    acceleration: evNavDefaultData.AccelerationAdjustment,
    savedTripsFolderUUID: undefined,
  }),
  mutations: {
    // Class object data mutators.
    [MutationTypes.setUser](state: State, payload: User | undefined) {
      state.user = payload;
    },
    [MutationTypes.setDriver](state: State, payload: Driver | undefined) {
      state.driver = payload;
    },
    [MutationTypes.setFleet](state: State, payload: Fleet | undefined) {
      state.fleet = payload;
    },
    [MutationTypes.setChargers](state: State, payload: Charger[]) {
      state.chargers = payload;
    },
    [MutationTypes.updateIndividualCharger](state: State, payload: Charger) {
      state.chargers = [
        ...state.chargers.filter((charger) => charger.id !== payload.id),
        payload,
      ];
    },
    [MutationTypes.setEvModels](state: State, payload: EVModel[]) {
      state.evModels = payload;
    },
    [MutationTypes.setTrips](state: State, payload: Trip[]) {
      state.trips = payload;
    },
    [MutationTypes.updateIndividualTrip](state: State, payload: Trip) {
      state.trips = [
        ...state.trips.filter((trip) => trip.localId !== payload.localId),
        payload,
      ];
    },
    [MutationTypes.setNetworkBranding](
      state: State,
      payload: NetworkBranding[]
    ) {
      state.networkBranding = payload;
    },
    [MutationTypes.setUserGeoIPData](
      state: State,
      payload: GeoLocation | undefined
    ) {
      state.userGeoIPData = payload;
    },
    [MutationTypes.setManagedContent](state: State, payload: ManagedContent[]) {
      state.managedContent = payload;
    },
    [MutationTypes.updateIndividualDriver](
      state: State,
      payload: Driver | undefined
    ) {
      state.driver = payload;
    },
    [MutationTypes.setVehicles](state: State, payload: Vehicle[]) {
      state.vehicles = payload;
    },
    [MutationTypes.updateIndividualVehicle](state: State, payload: Vehicle) {
      state.vehicles = [
        ...state.vehicles.filter(
          (vehicle) => vehicle.localId !== payload.localId
        ),
        payload,
      ];
    },
    [MutationTypes.addIndividualVehicle](state: State, payload: Vehicle) {
      state.vehicles = [...state.vehicles, payload];
    },
    [MutationTypes.setNearbyChargersData](
      state: State,
      payload: NearbyController | undefined
    ) {
      state.nearbyChargersData = payload;
    },
    // Ui viewing object data selection mutators.
    [MutationTypes.setSelectedCharger](
      state: State,
      payload: string | undefined
    ) {
      state.selectedCharger = payload;
    },
    [MutationTypes.setSelectedTrip](state: State, payload: string | undefined) {
      state.selectedTrip = payload;
    },
    [MutationTypes.setSelectedVehicle](
      state: State,
      payload: string | undefined
    ) {
      state.selectedVehicle = payload;
    },
    [MutationTypes.setSelectedLocation](
      state: State,
      payload: string | undefined
    ) {
      state.selectedLocation = payload;
    },
    [MutationTypes.setQueuedTripForDelete](
      state: State,
      payload: string | undefined
    ) {
      state.queuedTripForDelete = payload;
    },
    [MutationTypes.setViewedFleetVehicle](
      state: State,
      payload: string | undefined
    ) {
      state.viewedFleetVehicle = payload;
    },
    // async statuses.
    [MutationTypes.setDeleteTripStatus](
      state: State,
      payload: AsyncStatus | undefined
    ) {
      state.deleteTripStatus = payload;
    },
    [MutationTypes.setUpdateImageStatus](
      state: State,
      payload: AsyncStatus | undefined
    ) {
      state.updateImageStatus = payload;
    },
    [MutationTypes.setAuthStatus](
      state: State,
      payload: AsyncStatus | undefined
    ) {
      state.authStatus = payload;
    },
    [MutationTypes.setRoutePlanningStatus](
      state: State,
      payload: AsyncStatus | undefined
    ) {
      state.routePlanningStatus = payload;
    },
    [MutationTypes.setTripSavingStatus](
      state: State,
      payload: AsyncStatus | undefined
    ) {
      state.tripSavingStatus = payload;
    },
    [MutationTypes.setVehicleSavingStatus](
      state: State,
      payload: AsyncStatus | undefined
    ) {
      state.vehicleSavingStatus = payload;
    },
    [MutationTypes.setUpdatingUserStatus](
      state: State,
      payload: AsyncStatus | undefined
    ) {
      state.updatingUserStatus = payload;
    },
    [MutationTypes.setNearbyFetchingFlag](state: State, payload: boolean) {
      state.nearbyFetchingFlag = payload;
    },
    [MutationTypes.setRadarFetchingFlag](state: State, payload: boolean) {
      state.radarFetchingFlag = payload;
    },
    // Ui flag mutators.
    [MutationTypes.setFleetFetchingFlag](state: State, payload: boolean) {
      state.fleetFetchingFlag = payload;
    },
    [MutationTypes.setSettingsFetchingFlag](state: State, payload: boolean) {
      state.settingsFetchingFlag = payload;
    },
    [MutationTypes.setChargerFetchingFlag](state: State, payload: boolean) {
      state.chargerFetchingFlag = payload;
    },
    [MutationTypes.setTripFetchingFlag](state: State, payload: boolean) {
      state.tripFetchingFlag = payload;
    },
    [MutationTypes.setCheckingToken](state: State, payload: boolean) {
      state.checkingToken = payload;
    },
    [MutationTypes.setShowLogin](state: State, payload: boolean) {
      state.showLogin = payload;
    },
    [MutationTypes.setUserFetchingFlag](state: State, payload: boolean) {
      state.userFetchingFlag = payload;
    },
    [MutationTypes.setAuthorizedFlag](state: State, payload: boolean) {
      state.authorizedFlag = payload;
    },
    [MutationTypes.setRoutePlanningFlag](state: State, payload: boolean) {
      state.routePlanningFlag = payload;
    },
    [MutationTypes.setMainDialogContent](
      state: State,
      payload: MainDialogContent | undefined
    ) {
      state.mainDialogContent = payload;
    },
    [MutationTypes.setSecondaryDialogContent](
      state: State,
      payload: SecondaryDialogContent | undefined
    ) {
      state.secondaryDialogContent = payload;
    },
    [MutationTypes.setTertiaryDialogContent](
      state: State,
      payload: TertiaryDialogContent | undefined
    ) {
      state.tertiaryDialogContent = payload;
    },
    [MutationTypes.setChargersSearchCriteria](
      state: State,
      payload: string | undefined
    ) {
      state.chargersSearchCriteria = payload;
    },
    [MutationTypes.setVehiclesSearchCriteria](
      state: State,
      payload: string | undefined
    ) {
      state.vehiclesSearchCriteria = payload;
    },
    [MutationTypes.setShowNearbyChargersOnly](state: State, payload: boolean) {
      state.showNearbyChargersOnly = payload;
    },
    [MutationTypes.setShowPrivateACChargers](state: State, payload: boolean) {
      state.showPrivateACChargers = payload;
    },
    [MutationTypes.setShowPrivateDCChargers](state: State, payload: boolean) {
      state.showPrivateDCChargers = payload;
    },
    [MutationTypes.setShowPublicACChargers](state: State, payload: boolean) {
      state.showPublicACChargers = payload;
    },
    [MutationTypes.setShowPublicDCChargers](state: State, payload: boolean) {
      state.showPublicDCChargers = payload;
    },
    [MutationTypes.setDisplayAllChargersAlongRoute](
      state: State,
      payload: boolean
    ) {
      state.displayAllChargersAlongRoute = payload;
    },
    [MutationTypes.setDisplayLongestStep](state: State, payload: boolean) {
      state.displayLongestStep = payload;
    },
    [MutationTypes.setDisplayConnectedVehicles](
      state: State,
      payload: boolean
    ) {
      state.displayConnectedVehicles = payload;
    },
    // Settings mutators.
    [MutationTypes.setSettingsTableID](
      state: State,
      payload: number | undefined
    ) {
      state.settingsTableID = payload;
    },
    [MutationTypes.setSOCMax](state: State, payload: number) {
      state.SOCMax = payload;
      if (!state.settingsFetchingFlag && state.settingsTableID)
        updateSetting(state.settingsTableID, "Maximum_Charge", payload);
    },
    [MutationTypes.setSOCMin](state: State, payload: number) {
      state.SOCMin = payload;
      if (!state.settingsFetchingFlag && state.settingsTableID)
        updateSetting(state.settingsTableID, "Minimum_Charge", payload);
    },
    [MutationTypes.setSOCAct](state: State, payload: number) {
      state.SOCAct = payload;
    },
    [MutationTypes.setSOCEnd](state: State, payload: number) {
      state.SOCEnd = payload;
    },
    [MutationTypes.setDefaultPublicCostPerKWh](state: State, payload: number) {
      state.defaultPublicCostPerKWh = payload;

      if (!state.settingsFetchingFlag && state.settingsTableID)
        updateSetting(state.settingsTableID, "kWh_Cost_Public", payload);
    },
    [MutationTypes.setDefaultCostPerMinDC](state: State, payload: number) {
      state.defaultCostPerMinDC = payload;
      if (!state.settingsFetchingFlag && state.settingsTableID)
        updateSetting(
          state.settingsTableID,
          "Electricity_Cost_Per_Min_Public",
          payload
        );
    },
    [MutationTypes.setNetworkSelectionOverride](
      state: State,
      payload: string[] | undefined
    ) {
      state.networkSelectionOverride = payload;
      if (!state.settingsFetchingFlag && state.settingsTableID) {
        if (payload)
          updateSetting(
            state.settingsTableID,
            "Network_Selection_Override",
            JSON.stringify(payload)
          );
        else
          updateSetting(
            state.settingsTableID,
            "Network_Selection_Override",
            null
          );
      }
    },
    [MutationTypes.setDefaultHomeCostPerKWh](state: State, payload: number) {
      state.defaultHomeCostPerKWh = payload;
      if (!state.settingsFetchingFlag && state.settingsTableID)
        updateSetting(state.settingsTableID, "kWh_Cost_Home", payload);
    },
    [MutationTypes.setDieselCostPerLitre](state: State, payload: number) {
      state.dieselCostPerLitre = payload;
      if (!state.settingsFetchingFlag && state.settingsTableID)
        updateSetting(state.settingsTableID, "Diesel_Cost_Per_Litre", payload);
    },
    [MutationTypes.setDieselKmPerLitre](state: State, payload: number) {
      state.dieselKmPerLitre = payload;
      if (!state.settingsFetchingFlag && state.settingsTableID)
        updateSetting(state.settingsTableID, "Diesel_Km_Per_Litre", payload);
    },
    [MutationTypes.setExtraWeight](state: State, payload: number) {
      state.extraWeight = payload;
    },
    [MutationTypes.setRelativeSpeed](state: State, payload: number) {
      state.relativeSpeed = payload;
      if (!state.settingsFetchingFlag && state.settingsTableID)
        updateSetting(state.settingsTableID, "Relative_Speed", payload);
    },
    [MutationTypes.setAcceleration](state: State, payload: number) {
      state.acceleration = payload;
    },
    [MutationTypes.setPerAnnumMaintenanceCurrentV](
      state: State,
      payload: number
    ) {
      state.perAnnumMaintenanceCurrentV = payload;
      if (!state.settingsFetchingFlag && state.settingsTableID)
        updateSetting(
          state.settingsTableID,
          "Per_Annum_Maintenance_Current_V",
          payload
        );
    },
    [MutationTypes.setPerAnnumMaintenanceEV](state: State, payload: number) {
      state.perAnnumMaintenanceEV = payload;
      if (!state.settingsFetchingFlag && state.settingsTableID)
        updateSetting(
          state.settingsTableID,
          "Per_Annum_Maintenance_EV",
          payload
        );
    },
    [MutationTypes.setPetrolCostPerLitre](state: State, payload: number) {
      state.petrolCostPerLitre = payload;
      if (!state.settingsFetchingFlag && state.settingsTableID)
        updateSetting(state.settingsTableID, "Petrol_Cost_Per_Litre", payload);
    },
    [MutationTypes.setPetrolKmPerLitre](state: State, payload: number) {
      state.petrolKmPerLitre = payload;
      if (!state.settingsFetchingFlag && state.settingsTableID)
        updateSetting(state.settingsTableID, "Petrol_Km_Per_Litre", payload);
    },
    [MutationTypes.setRoadUserCharges](state: State, payload: number) {
      state.roadUserCharges = payload;
      if (!state.settingsFetchingFlag && state.settingsTableID)
        updateSetting(state.settingsTableID, "Road_User_Charges", payload);
    },
    [MutationTypes.setCalcVs](state: State, payload: CalcVsType) {
      state.calcVs = payload;
    },
    [MutationTypes.setSavedTripsFolderUUID](
      state: State,
      payload: string | undefined
    ) {
      state.savedTripsFolderUUID = payload;
    },
  },
  actions: {
    // -------------------------------------------------------------------- //
    // ---------------------- data fetching functions --------------------- //
    // -------------------------------------------------------------------- //

    // main fetching call chaining the other fetching methods from it.
    async [ActionTypes.fetchData]({
      commit,
      dispatch,
    }: {
      commit: Commit;
      dispatch: Dispatch;
    }) {
      const userData = await fetchUser();
      if (userData) {
        commit(MutationTypes.setUser, User.fromDirectusData(userData));
        dispatch(ActionTypes.getDriverData);
      }
      dispatch(ActionTypes.fetchDirectusSettingsData);
      dispatch(ActionTypes.getFleetData);
      dispatch(ActionTypes.getChargersAndNetworkData);
      dispatch(ActionTypes.getSettingsData);
      // TODO: add fetching call to get folder structure and save UUID for `Saved_Trips` folder to local state.
      // TODO: add fall back if folder dose not exist.
      const userGeoData = await fetchGeoIPDetails();
      if (userGeoData) {
        commit(
          MutationTypes.setUserGeoIPData,
          GeoLocation.fromMapsLittleMonkeyData(userGeoData)
        );
      }
      dispatch(ActionTypes.getManagedContent);
      commit(MutationTypes.setUserFetchingFlag, false);
    },
    // fetch fleet and fleet vehicle data.
    async [ActionTypes.getFleetData]({
      commit,
      dispatch,
    }: {
      commit: Commit;
      dispatch: Dispatch;
    }) {
      // Fetch data.
      const [
        companyData,
        configData,
        vehicleData,
        modelsData,
        modelImageLinks,
      ] = await Promise.all([
        fetchFleetData(),
        fetchFleetConfigData(),
        fetchFleetVehicles(),
        fetchEVmodels(),
        fetchEVModelImageLinks(),
      ]);
      // Check company data was successfully fetched.
      if (companyData) {
        // convert company into class object.
        const fleet = Fleet.fromDirectusData(companyData);
        if (configData) fleet.config = configData;
        // Add company data global state.
        commit(MutationTypes.setFleet, fleet);
        // add company wide settings to global state
        if (fleet.config) {
          if (fleet.config.acCostPerKWh)
            commit(
              MutationTypes.setDefaultHomeCostPerKWh,
              fleet.config.acCostPerKWh
            );
          if (fleet.config.dcCostPerKWh)
            commit(
              MutationTypes.setDefaultPublicCostPerKWh,
              fleet.config.dcCostPerKWh
            );
          if (fleet.config.petrolCostPerLitre)
            commit(
              MutationTypes.setPetrolCostPerLitre,
              fleet.config.petrolCostPerLitre
            );
          if (fleet.config.dieselCostPerLitre)
            commit(
              MutationTypes.setPetrolKmPerLitre,
              fleet.config.dieselCostPerLitre
            );
          if (fleet.config.litresPerKm)
            commit(MutationTypes.setPetrolKmPerLitre, fleet.config.litresPerKm);
        }
      }

      // Check if vehicles data was successfully fetched.
      if (vehicleData) {
        // Add vehicles data to global state.
        commit(
          MutationTypes.setVehicles,
          vehicleData.map((vehicle) => Vehicle.fromDirectusData(vehicle))
        );
      }

      // Check models data was successfully fetched.
      if (modelsData) {
        // Add models data to global state.
        if (modelImageLinks) {
          commit(
            MutationTypes.setEvModels,
            modelsData.map((model) => {
              const newModel = EVModel.fromChargerDbData(model);
              const imageLink = modelImageLinks.find(
                (link) => link.CDBID === model.id
              );
              if (imageLink) newModel.imageUUID = imageLink.Image;
              return newModel;
            })
          );
        } else {
          commit(
            MutationTypes.setEvModels,
            modelsData.map((model) => EVModel.fromChargerDbData(model))
          );
        }

        // Chain next step in fetched data sorting.
        dispatch(ActionTypes.addModelsToFleetVehicles);
        dispatch(ActionTypes.createGenericEVsFromModels);
        // update status
        commit(MutationTypes.setFleetFetchingFlag, false);
      }
    },

    async [ActionTypes.getDriverData]({
      commit,
      state,
    }: {
      commit: Commit;
      state: State;
    }) {
      if (state.user?.driverDirectusId) {
        const res = await fetchDriverData(state.user.driverDirectusId);
        if (res) {
          // Add company data global state.
          commit(
            MutationTypes.updateIndividualDriver,
            Driver.fromDirectusData(res)
          );
        }
      }
    },
    // fetch chargers and operating network data.
    async [ActionTypes.getChargersAndNetworkData]({
      commit,
    }: {
      commit: Commit;
    }) {
      // create variables to catch data.
      const tempChargersArray: Charger[] = [];
      const tempNetworkBrandingArray: NetworkBranding[] = [];

      // Fetch data.
      const [chargerData, networkBrandingData] = await Promise.all([
        fetchChargingStationsDb(),
        fetchNetworkBranding(),
      ]);

      // create "NetworkBranding" class objects from data.
      networkBrandingData?.forEach((network) => {
        tempNetworkBrandingArray.push(
          NetworkBranding.fromDirectusData(network)
        );
      });

      // get list of networks branding has been successfully gathered for.
      const networkList: string[] = [];
      tempNetworkBrandingArray.forEach((networkBranding) => {
        networkList.push(networkBranding.networkName);
        networkBranding.aliases.forEach((aliasObj) => {
          networkList.push(aliasObj.alias);
        });
      });

      // create "Charger" class objects from data.
      chargerData?.forEach((charger) => {
        try {
          tempChargersArray.push(Charger.fromChargerDbData(charger));
          // create additional `NetworkBranding` from networks for chargers that don't yet have branding records.
          if (
            charger.operator?.name &&
            !networkList.includes(charger.operator.name)
          ) {
            tempNetworkBrandingArray.push(
              new NetworkBranding({ networkName: charger.operator.name })
            );
            networkList.push(charger.operator.name);
          }
        } catch (error) {
          // analytics not yet contacted for this error as there is still a WIP peace on what to do with bad data in the charger DB.
        }
      });

      commit(MutationTypes.setChargers, tempChargersArray);
      commit(MutationTypes.setNetworkBranding, tempNetworkBrandingArray);
      // update status
      commit(MutationTypes.setChargerFetchingFlag, false);
    },
    // fetch saved trips and convert into `Trip` class objects.
    async [ActionTypes.getTripsData]({
      commit,
      dispatch,
      state,
    }: {
      commit: Commit;
      dispatch: Dispatch;
      state: State;
    }) {
      if (state.user?.directusId && state.savedTripsFolderUUID) {
        // fetch data
        const [savedTrips, savedOptimisedTrips, savedRoutePlans] =
          await Promise.all([
            fetchSavedTrips(state.user.directusId, state.savedTripsFolderUUID),
            fetchSavedOptimisedTripsByUser(state.user.directusId),
            fetchSavedRoutePlansByUser(state.user.directusId),
          ]);

        // sort data
        const tempArray: (Trip | TspTrip)[] = [];
        if (savedTrips) {
          tempArray.push(
            ...savedTrips.map((trip) => Trip.convertFromLegacySavedData(trip))
          );
        }
        if (savedOptimisedTrips) {
          tempArray.push(
            ...savedOptimisedTrips.map((trip) => TspTrip.fromDirectusData(trip))
          );
        }
        if (savedRoutePlans) {
          tempArray.push(
            ...savedRoutePlans.map((trip) => Trip.fromDirectusData(trip))
          );
        }

        // save to sate
        if (tempArray.length) commit(MutationTypes.setTrips, tempArray);

        // Chain next step in fetched data sorting.
        dispatch(ActionTypes.addVehiclesToTrips);
        // update status
        commit(MutationTypes.setTripFetchingFlag, false);
      }
    },
    // fetch settings data and apply to relevant state.
    async [ActionTypes.getSettingsData]({
      commit,
      state,
    }: {
      commit: Commit;
      state: State;
    }) {
      if (state.user?.directusId) {
        const fetchedData = await fetchSettings(state.user.directusId);
        if (fetchedData) {
          // overwrite defaults in setting exists/not null.
          if (fetchedData.Maximum_Charge)
            commit(MutationTypes.setSOCMax, fetchedData.Maximum_Charge);
          if (fetchedData.Minimum_Charge)
            commit(MutationTypes.setSOCMin, fetchedData.Minimum_Charge);
          if (fetchedData.Network_Selection_Override)
            commit(
              MutationTypes.setNetworkSelectionOverride,
              fetchedData.Network_Selection_Override
            );
          if (fetchedData.Electricity_Cost_Per_Min_Public)
            commit(
              MutationTypes.setDefaultCostPerMinDC,
              fetchedData.Electricity_Cost_Per_Min_Public
            );
          if (fetchedData.Relative_Speed)
            commit(MutationTypes.setRelativeSpeed, fetchedData.Relative_Speed);
          // preferring fleet settings overwrite defaults in setting exists/not null.
          //
          // NOTE: whe fleet data is grabbed this will also overwrite defaults this
          // code block assumes this will be handled separately by that process.
          if (fetchedData.kWh_Cost_Home && !state.fleet?.config?.acCostPerKWh)
            commit(
              MutationTypes.setDefaultHomeCostPerKWh,
              fetchedData.kWh_Cost_Home
            );
          if (fetchedData.kWh_Cost_Public && !state.fleet?.config?.dcCostPerKWh)
            commit(
              MutationTypes.setDefaultPublicCostPerKWh,
              fetchedData.kWh_Cost_Public
            );
          if (
            fetchedData.Petrol_Cost_Per_Litre &&
            !state.fleet?.config?.petrolCostPerLitre
          )
            commit(
              MutationTypes.setPetrolCostPerLitre,
              fetchedData.Petrol_Cost_Per_Litre
            );
          if (
            fetchedData.Petrol_Km_Per_Litre &&
            !state.fleet?.config?.litresPerKm
          )
            commit(
              MutationTypes.setPetrolKmPerLitre,
              fetchedData.Petrol_Km_Per_Litre
            );
          if (
            fetchedData.Diesel_Cost_Per_Litre &&
            !state.fleet?.config?.dieselCostPerLitre
          )
            commit(
              MutationTypes.setDieselCostPerLitre,
              fetchedData.Diesel_Cost_Per_Litre
            );
          if (fetchedData.Road_User_Charges)
            commit(
              MutationTypes.setRoadUserCharges,
              fetchedData.Road_User_Charges
            );
          if (fetchedData.Per_Annum_Maintenance_Current_V)
            commit(
              MutationTypes.setPerAnnumMaintenanceCurrentV,
              fetchedData.Per_Annum_Maintenance_Current_V
            );
          if (fetchedData.Per_Annum_Maintenance_EV)
            commit(
              MutationTypes.setPerAnnumMaintenanceEV,
              fetchedData.Per_Annum_Maintenance_EV
            );
          commit(MutationTypes.setSettingsTableID, fetchedData.id);
        }
        // update status
        commit(MutationTypes.setSettingsFetchingFlag, false);
      }
    },
    // fetch managed content.
    async [ActionTypes.getManagedContent]({ commit }: { commit: Commit }) {
      const managedContent = await fetchManagedContent();
      if (managedContent && managedContent.length)
        commit(MutationTypes.setManagedContent, managedContent);
    },
    // fetch directus instance specific settings
    async [ActionTypes.fetchDirectusSettingsData]({
      commit,
      dispatch,
    }: {
      commit: Commit;
      dispatch: Dispatch;
    }) {
      // attempt to fetch folder data
      const fetchedData = await fetchFolderDataByName(Saved_Trips);
      if (fetchedData) {
        commit(MutationTypes.setSavedTripsFolderUUID, fetchedData.id);
        dispatch(ActionTypes.getTripsData);
        return; // exit after successful operation.
      }
      // attempt to create folder data
      const createdData = await createDirectusFolder(Saved_Trips);
      if (createdData) {
        dispatch(ActionTypes.getTripsData);
        commit(MutationTypes.setSavedTripsFolderUUID, createdData.id);
        return; // exit after successful operation.
      }
      // last chance to catch any collisions with multiple users trying to create folder in between fetch and crate attempts.
      const lastChanceFetchData = await fetchFolderDataByName(Saved_Trips);
      if (lastChanceFetchData) {
        dispatch(ActionTypes.getTripsData);
        commit(MutationTypes.setSavedTripsFolderUUID, lastChanceFetchData.id);
        return; // exit after successful operation.
      }
      // exit with operation unsuccessful.
    },

    // -------------------------------------------------------------------- //
    // ----------------------- data sorting functions --------------------- //
    // -------------------------------------------------------------------- //

    // link fleet vehicles with EV models.
    [ActionTypes.addModelsToFleetVehicles]({
      commit,
      dispatch,
      state,
    }: {
      commit: Commit;
      dispatch: Dispatch;
      state: State;
    }) {
      state.vehicles.forEach((vehicle) => {
        const model = state.evModels.find(
          (model) => model.id === vehicle.eVModelId
        );
        if (model) {
          vehicle.setEVModel(model);
          commit(MutationTypes.updateIndividualVehicle, vehicle);
        }
        if (state.user?.driverDirectusId === vehicle.driverDirectusId) {
          commit(MutationTypes.setSelectedVehicle, vehicle.localId);
        }
      });
      // Chain next step in fetched data sorting.
      dispatch(ActionTypes.addVehiclesToTrips);
      dispatch(ActionTypes.checkSelectedVehicle);
    },

    // fall to set selected vehicle if one is not selected .
    [ActionTypes.checkSelectedVehicle]({
      commit,
      state,
      getters,
    }: {
      commit: Commit;
      state: State;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      getters: any;
    }) {
      if (!state.selectedVehicle && state.vehicles.length) {
        if (getters[GettersTypes.customVehicles].length) {
          commit(
            MutationTypes.setSelectedVehicle,
            getters[GettersTypes.customVehicles][0].localId
          );
          return;
        }
        if (getters[GettersTypes.connectedVehicles].length) {
          commit(
            MutationTypes.setSelectedVehicle,
            getters[GettersTypes.connectedVehicles][0].localId
          );
          return;
        }
        if (getters[GettersTypes.genericVehicles].length) {
          commit(
            MutationTypes.setSelectedVehicle,
            getters[GettersTypes.genericVehicles][0].localId
          );
          return;
        }
      }
    },

    // create generic EV's from models.
    [ActionTypes.createGenericEVsFromModels]({
      commit,
      dispatch,
      state,
    }: {
      commit: Commit;
      dispatch: Dispatch;
      state: State;
    }) {
      const tempArray: Vehicle[] = [];
      state.evModels.forEach((model) => {
        const newVehicle = new Vehicle({});
        newVehicle.setEVModel(model);
        newVehicle.name = "Generic EV";
        newVehicle.vehicleType = VehicleType.GENERIC_VEHICLE;
        tempArray.push(newVehicle);
      });
      commit(MutationTypes.setVehicles, [...state.vehicles, ...tempArray]);
      dispatch(ActionTypes.checkSelectedVehicle);
    },

    // link trips with fleet vehicles.
    [ActionTypes.addVehiclesToTrips]({ state }: { state: State }) {
      // check both fleet vehicle data and trips data has been fetched.
      if (state.vehicles.length && state.trips.length) {
        state.trips.forEach((trip) => {
          // check if trip has vehicle ID and vehicle data has not yet been added.
          if (trip.vehicleId && !trip.vehicle)
            trip.vehicle = state.vehicles.find(
              (vehicle) => vehicle.directusId === trip.vehicleId
            );
        });
      }
    },

    // -------------------------------------------------------------------- //
    // ---------------------- data clean up functions --------------------- //
    // -------------------------------------------------------------------- //

    // rest global state to default values.
    [ActionTypes.clearData]({ commit }: { commit: Commit }) {
      commit(MutationTypes.setAuthorizedFlag, false);
      commit(MutationTypes.setChargers, []);
      commit(
        MutationTypes.setDefaultCostPerMinDC,
        OptimiserDefaultData.electricityCostPerMinPublic
      );
      commit(
        MutationTypes.setDefaultPublicCostPerKWh,
        OptimiserDefaultData.kWhCostPublic
      );
      commit(MutationTypes.setEvModels, []);
      commit(MutationTypes.setFleet, undefined);
      commit(MutationTypes.setNetworkBranding, []);
      commit(MutationTypes.setRoutePlanningFlag, false);
      commit(MutationTypes.setSOCAct, OptimiserDefaultData.SOCAct);
      commit(MutationTypes.setSOCEnd, OptimiserDefaultData.SOCEnd);
      commit(MutationTypes.setSOCMax, OptimiserDefaultData.SOCMax);
      commit(MutationTypes.setSOCMin, OptimiserDefaultData.SOCMin);
      commit(MutationTypes.setNetworkSelectionOverride, undefined);
      commit(
        MutationTypes.setDefaultHomeCostPerKWh,
        OptimiserDefaultData.kWhCostHome
      );
      commit(
        MutationTypes.setRelativeSpeed,
        OptimiserDefaultData.relativeSpeed
      );
      commit(
        MutationTypes.setPetrolCostPerLitre,
        OptimiserDefaultData.petrolCostPerLitre
      );
      commit(
        MutationTypes.setPetrolKmPerLitre,
        OptimiserDefaultData.petrolKmPerLitre
      );
      commit(
        MutationTypes.setDieselCostPerLitre,
        OptimiserDefaultData.dieselCostPerLitre
      );
      commit(
        MutationTypes.setDieselKmPerLitre,
        OptimiserDefaultData.dieselKmPerLitre
      );
      commit(
        MutationTypes.setRoadUserCharges,
        OptimiserDefaultData.roadUserCharges
      );
      commit(
        MutationTypes.setPerAnnumMaintenanceCurrentV,
        OptimiserDefaultData.perAnnumMaintenanceCurrentV
      );
      commit(
        MutationTypes.setPerAnnumMaintenanceEV,
        OptimiserDefaultData.perAnnumMaintenanceEV
      );
      commit(MutationTypes.setCalcVs, CalcVsType.PETROL);
    },

    // clear status of target async operation after a delay.
    [ActionTypes.clearStatus](
      { commit, state }: { commit: Commit; state: State },
      mutationType: MutationTypes
    ) {
      // clear status after a given time.
      setTimeout(() => {
        commit(mutationType, undefined);
        // delayed exceptions
        if (mutationType === MutationTypes.setDeleteTripStatus) {
          commit(MutationTypes.setSecondaryDialogContent, undefined);
          commit(MutationTypes.setQueuedTripForDelete, undefined);
        }
        if (mutationType === MutationTypes.setUpdatingUserStatus) {
          commit(MutationTypes.setMainDialogContent, undefined);
        }
      }, 2000);

      // instant exceptions
      if (mutationType === MutationTypes.setDeleteTripStatus) {
        // check if selected trip needs to be cleared.
        if (state.queuedTripForDelete === state.selectedTrip) {
          commit(MutationTypes.setSelectedTrip, undefined);
        }
      }
    },

    // -------------------------------------------------------------------- //
    // ---------------------- data refresh functions ---------------------- //
    // -------------------------------------------------------------------- //

    // main data refresh call chaining other refresh methods from it.
    [ActionTypes.refreshData]({ dispatch }: { dispatch: Dispatch }) {
      dispatch(ActionTypes.updateVehiclesData);
    },

    // update fleet vehicles data.
    async [ActionTypes.updateVehiclesData]({
      commit,
      dispatch,
      state,
    }: {
      commit: Commit;
      dispatch: Dispatch;
      state: State;
    }) {
      if (state.fleet) {
        const vehiclesData = await fetchFleetVehicles();
        if (vehiclesData) {
          state.vehicles.forEach((vehicle) => {
            const updatedData = vehiclesData.find(
              (data) => data.id === vehicle.directusId
            );
            if (updatedData) {
              vehicle.updateData(updatedData);
              commit(MutationTypes.updateIndividualVehicle, vehicle);
            }
          });
        }
        // ensure updated vehicle objects are synced with trips vehicle objects.
        dispatch(ActionTypes.addVehiclesToTrips);
      } else {
        dispatch(ActionTypes.getFleetData);
      }
    },

    // -------------------------------------------------------------------- //
    // ---------------------- data update functions ----------------------- //
    // -------------------------------------------------------------------- //

    // update user/driver data
    async [ActionTypes.updateUser](
      {
        commit,
        dispatch,
        state,
        getters,
      }: {
        commit: Commit;
        dispatch: Dispatch;
        state: State;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        getters: any;
      },
      payload: DataToUpdate
    ) {
      const oldDriverData: Driver | undefined =
        getters[GettersTypes.currentDriver];

      commit(MutationTypes.setUpdatingUserStatus, AsyncStatus.PROCESSING);

      if (state.user) {
        const res = await updateUser(payload, state.user);
        // update state
        if (res.success && res.updatedDriver) {
          const newDriver = Driver.fromDirectusData(res.updatedDriver);
          if (oldDriverData?.localId) newDriver.localId = oldDriverData.localId;
          commit(MutationTypes.updateIndividualDriver, newDriver);
        }
        if (res.success && res.updatedUser) {
          const newUser = User.fromDirectusData(res.updatedUser);
          commit(MutationTypes.setUser, newUser);
        }
        // delete old avatar image
        if (res.success && oldDriverData?.profilePic) {
          await deleteFile(oldDriverData?.profilePic);
        }

        if (res.success) {
          commit(MutationTypes.setUpdatingUserStatus, AsyncStatus.SUCCESS);
          dispatch(
            ActionTypes.clearStatus,
            MutationTypes.setUpdatingUserStatus
          );
          return;
        }
      }

      commit(MutationTypes.setUpdatingUserStatus, AsyncStatus.FAILED);
      dispatch(ActionTypes.clearStatus, MutationTypes.setUpdatingUserStatus);
      return;
    },

    // updated selected vehicle and recalculate selected trip if there is
    // one and vehicle is different.
    async [ActionTypes.selectVehicle](
      {
        commit,
        dispatch,
        state,
        getters,
      }: {
        commit: Commit;
        dispatch: Dispatch;
        state: State;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        getters: any;
      },
      vehicle: Vehicle
    ) {
      // check if there is a selected trip that may need to be updated.
      if (state.selectedTrip) {
        const selectedTripData: Trip | undefined =
          getters[GettersTypes.selectedTripData];
        if (selectedTripData?.vehicleId !== vehicle.directusId) {
          selectedTripData?.setVehicle(vehicle);
          dispatch(ActionTypes.recalculateTrip, selectedTripData);
        }
      }

      if (state.selectedVehicle !== vehicle.localId) {
        // update state.
        commit(MutationTypes.setSelectedVehicle, vehicle.localId);
        // check if DB needs to be updated.
        if (vehicle.directusId) {
          // check if user has a driver.
          const driverRecord = state.driver?.directusId
            ? await updateDriverRecord(state.driver.directusId, {
                Driving: vehicle.directusId,
              }) // update driver record driving.
            : await createDriverRecord({
                Name: state.user?.fullName,
                Driving: vehicle.directusId,
              }); // create driver record.
          if (driverRecord) {
            // update sate.
            commit(
              MutationTypes.setDriver,
              Driver.fromDirectusData(driverRecord)
            );
            // update vehicle.
            await updateVehicle(vehicle.directusId, {
              Driver: driverRecord.id,
            });
            const updatedVehicle = vehicle;
            updatedVehicle.driverDirectusId = driverRecord.id;
            commit(MutationTypes.updateIndividualVehicle, updatedVehicle);
          }
        }
      }
    },

    async [ActionTypes.uploadVehicleImage](
      {
        commit,
        dispatch,
      }: {
        commit: Commit;
        dispatch: Dispatch;
      },
      { vehicle, imageFile }: UploadVehicleImagePayload
    ) {
      commit(MutationTypes.setUpdateImageStatus, AsyncStatus.PROCESSING);

      // attempt to upload image to cloud storage and update data.
      const outcome = await vehicle.uploadImage(imageFile);
      if (outcome === "success") {
        commit(MutationTypes.setUpdateImageStatus, AsyncStatus.SUCCESS);
        commit(MutationTypes.updateIndividualVehicle, vehicle);
        dispatch(ActionTypes.clearStatus, MutationTypes.setUpdateImageStatus);
        return;
      }

      // ASSUMES: failed if function reaches this point.
      commit(MutationTypes.setUpdateImageStatus, AsyncStatus.FAILED);
      dispatch(ActionTypes.clearStatus, MutationTypes.setUpdateImageStatus);
      return;
    },

    async [ActionTypes.uploadStationImage](
      {
        commit,
        dispatch,
        getters,
      }: {
        commit: Commit;
        dispatch: Dispatch;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        getters: any;
      },
      imageFile: File
    ) {
      // update async status to processing
      // find charger class object of target charger.
      const charger: Charger | undefined =
        getters[GettersTypes.selectedChargerData];
      if (!charger) {
        // exit early if no charger found and update async status to failed
        commit(MutationTypes.setUpdateImageStatus, AsyncStatus.FAILED);
        dispatch(ActionTypes.clearStatus, MutationTypes.setUpdateImageStatus);
        return;
      }
      commit(MutationTypes.setUpdateImageStatus, AsyncStatus.PROCESSING);

      // attempt to upload image to cloud storage and update data.
      const outcome = await charger.uploadImage(imageFile);
      if (outcome === "success") {
        commit(MutationTypes.setUpdateImageStatus, AsyncStatus.SUCCESS);
        commit(MutationTypes.updateIndividualCharger, charger);
        dispatch(ActionTypes.clearStatus, MutationTypes.setUpdateImageStatus);
        return;
      }

      // ASSUMES: failed if function reaches this point.
      commit(MutationTypes.setUpdateImageStatus, AsyncStatus.FAILED);
      dispatch(ActionTypes.clearStatus, MutationTypes.setUpdateImageStatus);
      return;
    },

    async [ActionTypes.saveVehicle](
      {
        commit,
        dispatch,
        state,
      }: {
        commit: Commit;
        dispatch: Dispatch;
        state: State;
      },
      newVehicle: Vehicle
    ) {
      commit(MutationTypes.setVehicleSavingStatus, AsyncStatus.PROCESSING);
      const vehicle = newVehicle;
      const saveStatus = await vehicle.saveVehicle();
      if (saveStatus === "success") {
        if (vehicle.directusId && state.user?.directusId) {
          const car: Car_User = {
            Car_Record_id: vehicle.directusId,
            directus_users_id: state.user.directusId,
          };
          dispatch(ActionTypes.updateUser, { Cars: [car] });
        }
        commit(MutationTypes.setVehicleSavingStatus, AsyncStatus.SUCCESS);
      } else {
        commit(MutationTypes.setVehicleSavingStatus, AsyncStatus.FAILED);
      }
      commit(MutationTypes.addIndividualVehicle, vehicle);
      dispatch(ActionTypes.clearStatus, MutationTypes.setVehicleSavingStatus);
    },

    // -------------------------------------------------------------------- //
    // ----------------------- Trip Specific Actions ---------------------- //
    // -------------------------------------------------------------------- //

    async [ActionTypes.showTrip](
      { dispatch }: { dispatch: Dispatch },
      trip: Trip | TspTrip
    ) {
      if (trip instanceof Trip) dispatch(ActionTypes.showPlannedTrip, trip);
      if (trip instanceof TspTrip)
        dispatch(ActionTypes.showPlannedTspTrip, trip);
    },

    // Plan and display the passed TspTrip class object.
    async [ActionTypes.showPlannedTspTrip](
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      trip: TspTrip
    ) {
      // ensure no nearby data is showing.
      commit(MutationTypes.setShowNearbyChargersOnly, false);
      // update state.
      commit(MutationTypes.setRoutePlanningStatus, AsyncStatus.PROCESSING);
      commit(MutationTypes.setSelectedTrip, trip.localId);
      router.push({ name: RouteNames.itinerary });
      const planningStatus = await trip.planTrip();
      if (planningStatus === TspTripPlanningStatus.errorNotEnoughLocations) {
        commit(MutationTypes.setRoutePlanningStatus, AsyncStatus.FAILED);
      }
      if (planningStatus === TspTripPlanningStatus.errorNotRoutable) {
        commit(MutationTypes.setRoutePlanningStatus, AsyncStatus.FAILED);
      }
      if (planningStatus === TspTripPlanningStatus.success) {
        commit(MutationTypes.setRoutePlanningStatus, AsyncStatus.SUCCESS);
      }
      commit(MutationTypes.updateIndividualTrip, trip);
      // set cleanup in progress.
      dispatch(ActionTypes.clearStatus, MutationTypes.setRoutePlanningStatus);
    },

    // Plan and display the passed trip class object.
    async [ActionTypes.showPlannedTrip](
      {
        commit,
        dispatch,
        state,
      }: { commit: Commit; dispatch: Dispatch; state: State },
      trip: Trip
    ) {
      // ensure no nearby data is showing.
      commit(MutationTypes.setShowNearbyChargersOnly, false);
      // update state.
      commit(MutationTypes.setRoutePlanningStatus, AsyncStatus.PROCESSING);
      commit(MutationTypes.setSelectedTrip, trip.localId);
      router.push({ name: RouteNames.itinerary });
      const planningStatus = await trip.planRoute(false, state.relativeSpeed);
      if (
        planningStatus === "EV-SUCCESS" ||
        planningStatus === "ICE-SUCCESS" ||
        planningStatus === "FALLBACK"
      )
        commit(MutationTypes.setRoutePlanningStatus, AsyncStatus.SUCCESS);
      if (planningStatus === "FAILED")
        commit(MutationTypes.setRoutePlanningStatus, AsyncStatus.FAILED);

      commit(MutationTypes.updateIndividualTrip, trip);
      // set cleanup in progress.
      dispatch(ActionTypes.clearStatus, MutationTypes.setRoutePlanningStatus);
      // exit the function.
      return;
    },

    // create a new populated Trip class object and call show trp action with this object.
    [ActionTypes.createNewTrip](
      { dispatch, state }: { dispatch: Dispatch; state: State },
      payload: TripPlanningFormData
    ) {
      const newTrip = new Trip({
        locations: payload.locations,
        frequency: payload.frequency,
        stateOfChargeMin: payload.SOCMin ?? state.SOCMin,
        stateOfChargeMax: payload.SOCMax ?? state.SOCMax,
        stateOfChargeAct: payload.SOCAct ?? state.SOCAct,
        stateOfChargeEnd: payload.SOCEnd ?? state.SOCEnd,
        primaryTimeLocation: payload.primaryTimeLocation,
        extraWeight: state.extraWeight,
        userSpecifiedNetworks: state.networkSelectionOverride,
      });

      newTrip.setVehicle(payload.vehicle);
      dispatch(ActionTypes.showPlannedTrip, newTrip);
    },

    // recalculate and display a trip.
    [ActionTypes.recalculateTrip](
      { dispatch }: { dispatch: Dispatch },
      trip: Trip
    ) {
      trip.prepForRecalculation();
      dispatch(ActionTypes.showPlannedTrip, trip);
    },

    // name and save individual trip.
    [ActionTypes.nameAndSaveTrip](
      {
        commit,
        dispatch,
        state,
      }: {
        commit: Commit;
        dispatch: Dispatch;
        state: State;
      },
      {
        tripID,
        name,
      }: {
        tripID: string;
        name: string;
      }
    ) {
      const tripToSave = state.trips.find((trip) => trip.localId === tripID);
      if (!tripToSave) return;
      tripToSave.name = name;
      commit(MutationTypes.updateIndividualTrip, tripToSave);
      dispatch(ActionTypes.saveTrip, tripID);
    },

    // save individual trip data
    async [ActionTypes.saveTrip](
      {
        commit,
        state,
        dispatch,
      }: { commit: Commit; state: State; dispatch: Dispatch },
      tripID: string
    ) {
      commit(MutationTypes.setTripSavingStatus, AsyncStatus.PROCESSING);
      const tripToSave = state.trips.find((trip) => trip.localId === tripID);
      if (!tripToSave) {
        // no trip found update status and exit early.
        commit(MutationTypes.setTripSavingStatus, AsyncStatus.FAILED);
        dispatch(ActionTypes.clearStatus, MutationTypes.setTripSavingStatus);
        return;
      }

      const outcome = await tripToSave.saveTrip();
      if (outcome === "ok") {
        // successful operation update status and exit.
        commit(MutationTypes.setTripSavingStatus, AsyncStatus.SUCCESS);
        commit(MutationTypes.updateIndividualTrip, tripToSave);
        dispatch(ActionTypes.clearStatus, MutationTypes.setTripSavingStatus);
        return;
      }

      if (outcome === "failed") {
        // failed operation update status and exit.
        commit(MutationTypes.setTripSavingStatus, AsyncStatus.FAILED);
        dispatch(ActionTypes.clearStatus, MutationTypes.setTripSavingStatus);
        return;
      }
    },

    // delete the trip currently queued for deletion.
    async [ActionTypes.deleteTrip]({
      commit,
      dispatch,
      state,
      getters,
    }: {
      commit: Commit;
      dispatch: Dispatch;
      state: State;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      getters: any;
    }) {
      commit(MutationTypes.setDeleteTripStatus, AsyncStatus.PROCESSING);
      const tripToDelete: Trip | TspTrip | undefined =
        getters[GettersTypes.queuedForDeleteTripData];

      // Trip not found guard clause.
      if (!tripToDelete) {
        commit(MutationTypes.setDeleteTripStatus, AsyncStatus.FAILED);
        dispatch(ActionTypes.clearStatus, MutationTypes.setDeleteTripStatus);
        return;
      }

      // Remove trip locally.
      commit(
        MutationTypes.setTrips,
        state.trips.filter((trip) => trip.localId !== tripToDelete.localId)
      );

      // Check if trip has been saved remotely.
      if (tripToDelete.directusId) {
        // Check type of trip
        // if `TspTrip` remove from directus only.
        // if `Trip` remove JSON from S3 storage and record from directus.
        const deleteOutcome =
          tripToDelete instanceof TspTrip
            ? await deleteSavedOptimisedTrip(tripToDelete.directusId)
            : tripToDelete.needsConverting
            ? await deleteSavedTrip(tripToDelete)
            : await deleteSavedRoutePlans(tripToDelete.directusId);
        // set status outcome.
        if (deleteOutcome === "ok")
          commit(MutationTypes.setDeleteTripStatus, AsyncStatus.SUCCESS);
        if (deleteOutcome === "failed")
          commit(MutationTypes.setDeleteTripStatus, AsyncStatus.FAILED);
        // exit function.
        dispatch(ActionTypes.clearStatus, MutationTypes.setDeleteTripStatus);
        return;
      }

      // return outcome of local only deletion.
      // ASSUMES if not exited in above block this was not a trip that was saved remotely.
      commit(MutationTypes.setDeleteTripStatus, AsyncStatus.SUCCESS);
      // clear status after a given period.
      dispatch(ActionTypes.clearStatus, MutationTypes.setDeleteTripStatus);
      return;
    },

    // get the longest leap leg data for the currently selected trip.
    async [ActionTypes.showAllChargersOnRoute]({
      commit,
      getters,
    }: {
      commit: Commit;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      getters: any;
    }) {
      commit(MutationTypes.setRadarFetchingFlag, true);
      const trip: Trip | undefined = getters[GettersTypes.selectedTripData];
      if (!trip) return;
      const outcome = await trip.getRadarData();
      if (outcome === "SUCCESS") {
        commit(MutationTypes.updateIndividualTrip, trip);
        commit(MutationTypes.setDisplayAllChargersAlongRoute, true);
      }
      commit(MutationTypes.setRadarFetchingFlag, false);
    },

    // get nearby charger data for nearbyCall functionality.
    async [ActionTypes.fetchNearbyChargersData](
      { commit }: { commit: Commit },
      payload: EVNavNearbyParams
    ) {
      // clear state
      commit(MutationTypes.setShowNearbyChargersOnly, false);
      // set async operation flag
      commit(MutationTypes.setNearbyFetchingFlag, true);
      // create new class object
      const nearbyController = new NearbyController(payload);
      // fetch data
      const outcome = await nearbyController.fetchData();
      // check if successful
      if (outcome === "SUCCESS") {
        // ensure no trip is showing
        commit(MutationTypes.setSelectedTrip, undefined);
        // update state
        commit(MutationTypes.setNearbyChargersData, nearbyController);
        commit(MutationTypes.setShowNearbyChargersOnly, true);
      }
      // clear async operation flag
      commit(MutationTypes.setNearbyFetchingFlag, false);
    },

    // -------------------------------------------------------------------- //
    // -------------------------- auth functions -------------------------- //
    // -------------------------------------------------------------------- //

    // login call.
    async [ActionTypes.login](
      { dispatch, commit }: { dispatch: Dispatch; commit: Commit },
      credentials: DirectusLoginParams
    ) {
      commit(MutationTypes.setAuthStatus, AsyncStatus.PROCESSING);
      const authenticated = await login(credentials);

      if (authenticated) {
        commit(MutationTypes.setAuthStatus, AsyncStatus.SUCCESS);
        commit(MutationTypes.setAuthorizedFlag, true);
        dispatch(ActionTypes.fetchData);
        setTimeout(() => {
          commit(MutationTypes.setShowLogin, false);
          commit(MutationTypes.setAuthStatus, undefined);
        }, 100);
      } else {
        commit(MutationTypes.setAuthStatus, AsyncStatus.FAILED);
      }
    },

    // logout and clean up call.
    [ActionTypes.logoutUser]({
      dispatch,
      commit,
    }: {
      dispatch: Dispatch;
      commit: Commit;
    }) {
      commit(MutationTypes.setShowLogin, true);
      logout();
      dispatch(ActionTypes.clearData);
    },

    // request forgot password email call.
    [ActionTypes.forgotPassword](
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      { commit }: { commit: Commit },
      email: string
    ) {
      resetPassword(email);
    },
  },
  getters: {
    // get the currently selected trip object.
    [GettersTypes.selectedTripData](state: State): Trip | TspTrip | undefined {
      return state.trips.find((trip) => trip.localId === state.selectedTrip);
    },
    // get the currently selected vehicle object.
    [GettersTypes.selectedVehicleData](state: State): Vehicle | undefined {
      return state.vehicles.find(
        (vehicle) => vehicle.localId === state.selectedVehicle
      );
    },
    // get all connected telematics vehicles.
    [GettersTypes.connectedVehicles](state: State): Vehicle[] {
      return state.vehicles.filter(
        (vehicle) => vehicle.vehicleType === VehicleType.TELEMATICS_VEHICLE
      );
    },
    // get all generic vehicles.
    [GettersTypes.genericVehicles](state: State): Vehicle[] {
      return state.vehicles.filter(
        (vehicle) => vehicle.vehicleType === VehicleType.GENERIC_VEHICLE
      );
    },
    // get all custom vehicles.
    [GettersTypes.customVehicles](state: State): Vehicle[] {
      return state.vehicles.filter(
        (vehicle) => vehicle.vehicleType === VehicleType.CUSTOM_VEHICLE
      );
    },
    // get the currently selected charger object.
    [GettersTypes.selectedChargerData](state: State): Charger | undefined {
      return state.chargers.find(
        (charger) => charger.id === state.selectedCharger
      );
    },
    // get the network branding for the currently selected charger.
    [GettersTypes.selectedChargerBranding](
      state: State
    ): NetworkBranding | undefined {
      const network = state.chargers.find(
        (charger) => charger.id === state.selectedCharger
      )?.operator?.name;
      if (!network) return;
      return state.networkBranding.find((branding) =>
        branding.isThisNetwork(network)
      );
    },
    // get a list of filtered charges for display.
    [GettersTypes.filterChargingStations](state: State, getters): Charger[] {
      const trip: Trip | TspTrip | undefined =
        getters[GettersTypes.selectedTripData];
      if (trip && trip instanceof Trip && state.displayAllChargersAlongRoute) {
        // Show all chargers along route
        const tempArray: Charger[] = [];
        trip.chargersAlongRouteCDBIDs.forEach((CDBID) => {
          const charger = state.chargers.find(
            (charger) => charger.id === CDBID
          );
          if (charger) tempArray.push(charger);
        });
        return tempArray;
      }
      if (trip && trip instanceof Trip) {
        // Show only trip charging stops
        const tempArray: Charger[] = [];
        trip.chargingStopCDBIDs.forEach((CDBID) => {
          const charger = state.chargers.find(
            (charger) => charger.id === CDBID
          );
          if (charger) tempArray.push(charger);
        });
        return tempArray;
      }

      if (
        state.showNearbyChargersOnly &&
        state.nearbyChargersData?.nearbyData
      ) {
        // show only near by chargers.
        const tempArray: Charger[] = [];
        state.nearbyChargersData.nearbyData.forEach((evNavCharger) => {
          const charger = state.chargers.find(
            (charger) => charger.id === evNavCharger.Charger.CDBID
          );
          if (charger) tempArray.push(charger);
        });
        return tempArray;
      }

      let returnArray: Charger[] = state.chargers;

      // check if network selection override is being used
      if (
        state.networkSelectionOverride &&
        state.networkSelectionOverride.length
      ) {
        // filter out chargers that don't match network selection
        const tempArray: Charger[] = [];
        returnArray.forEach((charger) => {
          if (
            charger.operator?.name &&
            state.networkSelectionOverride?.includes(charger.operator.name)
          )
            tempArray.push(charger);
        });
        // Update return array.
        returnArray = tempArray;
      }

      // check if there is search criteria
      if (state.chargersSearchCriteria) {
        // lowercase searchCriteria
        const lowercaseSearchCriteria =
          state.chargersSearchCriteria.toLowerCase();
        // filter out chargers that don't match search criteria
        const tempArray: Charger[] = [];
        returnArray.forEach((charger) => {
          if (
            charger.name?.toLowerCase().includes(lowercaseSearchCriteria) ||
            charger.addressString
              .toLowerCase()
              .includes(lowercaseSearchCriteria)
          )
            tempArray.push(charger);
        });
        // Update return array.
        returnArray = tempArray;
      }

      // gather filters
      const filters: ChargerFilters[] = [];

      // check if private AC chargers are not meant to be showing and need to be filtered.
      if (!state.showPrivateACChargers)
        filters.push(ChargerFilters.NO_PRIVATE_AC);
      // check if private DC chargers are not meant to be showing and need to be filtered.
      if (!state.showPrivateDCChargers)
        filters.push(ChargerFilters.NO_PRIVATE_DC);
      // check if public AC chargers are not meant to be showing and need to be filtered.
      if (!state.showPublicACChargers)
        filters.push(ChargerFilters.NO_PUBLIC_AC);
      // check if public DC chargers are not meant to be showing and need to be filtered.
      if (!state.showPublicDCChargers)
        filters.push(ChargerFilters.NO_PUBLIC_DC);

      if (filters.length) {
        // filter out chargers that don't match search criteria
        const tempArray: Charger[] = [];
        returnArray.forEach((charger) => {
          if (!charger.excludeByFilters(filters)) tempArray.push(charger);
        });
        // Update return array.
        returnArray = tempArray;
      }

      // Return filtered array.
      return returnArray;
    },
    // get the current users fleet driver data.
    [GettersTypes.currentDriver](state: State): Driver | undefined {
      return state.driver;
    },
    // get the current tripes selected trip location object.
    [GettersTypes.selectedLocationData](
      state: State,
      getters
    ): TripLocation | undefined {
      const trip: Trip | undefined = getters[GettersTypes.selectedTripData];
      return trip?.locations.find(
        (location) => location.localId === state.selectedLocation
      );
    },
    // get the trip data for the trip that is currently queued for deletion.
    [GettersTypes.queuedForDeleteTripData](
      state: State
    ): Trip | TspTrip | undefined {
      return state.trips.find(
        (trip) => trip.localId === state.queuedTripForDelete
      );
    },
    // get stats for the currently selected trip
    [GettersTypes.selectedTripStats](
      state: State,
      getters
    ): TripStats | undefined {
      const trip: Trip | undefined = getters[GettersTypes.selectedTripData];
      return trip?.getTripStats({
        calcVs: state.calcVs,
        petrolCostPerLitre: state.petrolCostPerLitre,
        petrolKmPerLitre: state.petrolKmPerLitre,
        dieselCostPerLitre: state.dieselCostPerLitre,
        dieselKmPerLitre: state.dieselKmPerLitre,
        kWhCostHome: state.defaultHomeCostPerKWh,
      });
    },
    // get 5yr predictions on savings
    [GettersTypes.getFiveYearProjections](state: State): FiveYearSavings {
      return calcFiveYearSavings(
        state.trips.filter((trip) => trip instanceof Trip) as Trip[],
        {
          calcVs: state.calcVs,
          dieselCostPerLitre: state.dieselCostPerLitre,
          dieselKmPerLitre: state.dieselKmPerLitre,
          kWhCostHome: state.defaultHomeCostPerKWh,
          perAnnumMaintenanceCurrentV: state.perAnnumMaintenanceCurrentV,
          perAnnumMaintenanceEV: state.perAnnumMaintenanceEV,
          petrolCostPerLitre: state.petrolCostPerLitre,
          petrolKmPerLitre: state.petrolKmPerLitre,
          roadUserCharges: state.roadUserCharges,
        }
      );
    },
    // get the currently viewed fleet vehicles data if there is one.
    [GettersTypes.viewedFleetVehicleData](state: State): Vehicle | undefined {
      return state.vehicles.find(
        (vehicle) => vehicle.localId === state.viewedFleetVehicle
      );
    },
    // get the current vehicle out of viewed fleet vehicle or selected vehicle.
    [GettersTypes.displayedVehicle](
      state: State,
      getters
    ): Vehicle | undefined {
      if (state.viewedFleetVehicle)
        return getters[GettersTypes.viewedFleetVehicleData];
      return getters[GettersTypes.selectedVehicleData];
    },
    // get wether to show the fallback trips longest leap polyline or not.
    [GettersTypes.showFallbackLongestLeap](state: State, getters): boolean {
      const trip: Trip | undefined = getters[GettersTypes.selectedTripData];
      if (!trip) return false;
      if (
        trip.status === "FALLBACK" &&
        trip.fallbackCause?.reason ===
          TripFallbackCauseReasons.LONGEST_LEAP_MORE_THAN_FULL_CHARGE
      )
        return true;
      return false;
    },
    // get a filtered list of trips that have been saved
    [GettersTypes.savedTrips](state: State): (Trip | TspTrip)[] {
      return state.trips.filter((trip) => !!trip.directusId);
    },
    // get a filtered list of trips that have not been saved
    [GettersTypes.unsavedTrips](state: State): (Trip | TspTrip)[] {
      return state.trips.filter((trip) => !trip.directusId);
    },
  },
  modules: {},
});
